import React, { useState, useEffect, useCallback, useContext } from "react";
import useFolders from "../../shared/hooks/useFolders";
import { useHttpClient } from "../../shared/hooks/http-hook";
import { AuthContext } from "../../shared/context/auth-context";
import FileModal from "./FileModal";
import { FaFileAudio, FaFileVideo } from "react-icons/fa";
import ReactPlayer from "react-player";
import DragAndDropFileUploadRow from "./DragAndDropFileUploadRow";
import { Modal, Spinner } from "react-bootstrap";

function FileRow({ file, folderId, onDeleteFile }) {
	const [showModal, setShowModal] = useState(false);
	const { sendRequest } = useHttpClient();
	const [videoUrl, setVideoUrl] = useState(null);

	const isImage = file.type?.startsWith("image/");
	const isAudio = file.type?.startsWith("audio/");
	const isVideo = file.type?.startsWith("video/");

	const handleRowClick = async () => {
		if (file.type === "video") {
			try {
				const response = await sendRequest(
					`/api/videos/${file.videoId}/details`
				);
				console.log("Video details:", response.data.video_url);
				setVideoUrl(response.data.video_url);
			} catch (err) {
				console.error("Error fetching video details:", err);
			}
		}
		setShowModal(true);
	};

	// A function to render the card thumbnail/icon
	function renderCardPreview() {
		if (isImage) {
			// Show a small image as preview (thumbnail)
			return (
				<img
					src={file.url}
					alt={file.name}
					style={{ width: "30px", height: "auto" }}
				/>
			);
		} else if (isAudio) {
			// Show an audio icon
			return (
				//center the icon vertically and horizontally
				<FaFileAudio
					className="fs-1"
					style={{ width: "30px", height: "auto", color: "#D1D5DB" }}
				/>
			);
		} else if (isVideo) {
			// If you have a real thumbnail, show it. Otherwise, show a video icon
			return (
				<FaFileVideo
					className="fs-1"
					style={{ width: "30px", height: "auto", color: "#D1D5DB" }}
				/>
			);
		} else if (file.type === "video") {
			return (
				<img
					src={file.thumbnail_url}
					alt={file.name}
					style={{ width: "40px", height: "auto" }}
				/>
			);
		} else {
			// Generic file icon
			return <i className="bi bi-file-earmark fs-1"></i>;
		}
	}

	function renderModalContent() {
		if (isImage) {
			return (
				<div>
					<a href={file.url} target="_blank" rel="noopener noreferrer">
						Download
					</a>
					<img
						src={file.url}
						alt={file.name}
						style={{ width: "100%", height: "auto" }}
					/>
				</div>
			);
		} else if (isAudio) {
			return (
				<div>
					<a href={file.url} target="_blank" rel="noopener noreferrer">
						Download
					</a>
					<audio controls style={{ width: "100%" }}>
						<source src={file.url} type={file.type} />
						Your browser does not support the audio element.
					</audio>
				</div>
			);
		} else if (isVideo) {
			return (
				<div>
					<a href={file.url} target="_blank" rel="noopener noreferrer">
						Download
					</a>
					<video controls style={{ width: "100%" }}>
						<source src={file.url} type={file.type} />
						Your browser does not support the video element.
					</video>
				</div>
			);
		} else if (file.type === "video") {
			return <ReactPlayer url={videoUrl} controls={true} width="100%" />;
		} else {
			return (
				<p>
					No preview available.
					<a href={file.url} target="_blank" rel="noopener noreferrer">
						Download
					</a>
				</p>
			);
		}
	}

	return (
		<React.Fragment>
			<tr
				className="border-b hover:bg-gray-50 cursor-pointer"
				onClick={handleRowClick}
				key={file.id}
			>
				<td className="px-6 py-3 text-sm">{renderCardPreview()}</td>
				<td className="px-6 py-3 text-sm">{file.name}</td>
				<td className="px-6 py-3 text-sm">
					{/* Example: Just show the file type or do more advanced logic */}
					{file.type}
				</td>
				{/* download button */}
				<td className="px-6 py-3 text-sm">
					<a href={file.url} target="_blank" rel="noopener noreferrer">
						Download
					</a>
				</td>
			</tr>
			<FileModal
				show={showModal}
				onHide={() => setShowModal(false)}
				folderId={folderId}
				fileId={file.id}
				name={file.name}
				onDelete={onDeleteFile}
			>
				{renderModalContent()}
			</FileModal>
		</React.Fragment>
	);
}

export default function TicketAssets({
	title,
	folderId,
	orgId,
	onFolderSelect,
	locked,
	ticketId,
}) {
	const { folders, createFolder, fetchFolders, uploadFile, uploading } =
		useFolders();
	const { sendRequest } = useHttpClient();
	const authContext = useContext(AuthContext);
	const [selectedFolder, setSelectedFolder] = useState(folderId || "");
	const [files, setFiles] = useState([]);
	
	// Debug: Log files state changes
	useEffect(() => {
		console.log("Files state updated:", files);
	}, [files]);
	const [isLoadingFiles, setIsLoadingFiles] = useState(false);
	const [error, setError] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);

	const handleUpload = async (file, fileTitle) => {
		if (!file) return;
		try {
			setIsLoadingFiles(true);
			let finalFolderId = folderId;
			if (!finalFolderId) {
				const newFolder = await createFolder(title, orgId);
				finalFolderId = newFolder.id;

				onFolderSelect(finalFolderId);
				setSelectedFolder(finalFolderId);

				await fetchFolders();
			}

			await uploadFile(file, fileTitle, finalFolderId, orgId);

			setIsLoadingFiles(false);
			setSelectedFile(null);

			await fetchFiles(finalFolderId);
		} catch (err) {
			console.error("Error uploading file:", err);
		}
	};

	const handleCreateFolder = async (title, orgId) => {
		if (!title) {
			setError("Folder name is required");
			return;
		}
		if (!orgId) {
			setError("Organization ID is required");
			return;
		}
		try {
			const respone = await createFolder(title, orgId);
			await fetchFolders();
			setSelectedFolder(respone.id);
			onFolderSelect(respone.id);
		} catch (err) {
			setError("Failed to create folder");
		}
		setError(null);
	};

	const handleFolderChange = (e) => {
		const newId = e.target.value;
		setSelectedFolder(newId);
		onFolderSelect(newId);
	};

	const fetchFiles = useCallback(
		async (folderIdOverride) => {
			const idToUse = folderIdOverride || selectedFolder;
			if (!idToUse) return;

			console.log("Fetching files for folder:", idToUse);
			setIsLoadingFiles(true);
			try {
				const hasTicket = ticketId ? `/${ticketId}` : "";
				const url = `/api/folders/${idToUse}/files${hasTicket}`;
				const response = await sendRequest(url);
				console.log("Files fetched:", response.data);
				setFiles(response.data);
			} catch (err) {
				console.error("Error fetching files:", err);
				setFiles([]);
			} finally {
				setIsLoadingFiles(false);
			}
		},
		[sendRequest, selectedFolder, ticketId]
	);

	const handleDeleteFile = async (fileId, folderId) => {
		try {
			console.log("Deleting file:", fileId, "from folder:", folderId);
			await sendRequest(
				`/api/folders/${folderId}/files/${fileId}/delete`,
				"DELETE",
				null,
				{
					Authorization: "Bearer " + authContext.token,
				}
			);
			console.log("File deleted successfully, refreshing files...");
			// Use the current selectedFolder or the folderId from props
			const folderToRefresh = selectedFolder || folderId;
			console.log("Refreshing folder:", folderToRefresh);
			
			// Force a re-render by updating files state directly first
			setFiles(prevFiles => {
				console.log("Removing deleted file from state:", fileId);
				return prevFiles.filter(file => file.id !== fileId);
			});
			
			// Then fetch fresh data
			await fetchFiles(folderToRefresh);
		} catch (err) {
			console.error("Error deleting file:", err);
			throw err; // Re-throw to let FileModal handle the error
		}
	};

	useEffect(() => {
		setSelectedFolder(folderId || "");
		fetchFiles();
	}, [folderId, fetchFiles]);

	return (
		<div className="flex flex-col space-y-4 p-4">
			{error && <div className="text-red-500 text-sm">{error}</div>}
			{!locked && (
				<div className="flex items-center justify-between">
					<select
						className="border px-3 py-2 rounded-md"
						value={selectedFolder}
						onChange={handleFolderChange}
					>
						<option value="">Select Folder</option>
						{folders.map((f) => (
							<option key={f.id} value={f.id}>
								{f.name}
							</option>
						))}
					</select>
					<button
						type="button"
						className="bg-blue-500 text-white px-4 py-2 rounded-md"
						onClick={() => handleCreateFolder(title, orgId)}
					>
						Create Folder
					</button>
				</div>
			)}
			<div className="w-full overflow-hidden border rounded-lg flex-grow flex flex-col min-h-0 mt-4">
				<style>
					{`
            .scrollable-table {
              max-height: 340px; /* change as needed */
              overflow-y: auto; 
              scrollbar-gutter: stable;
            }
            
            .scrollable-table::-webkit-scrollbar {
              width: 8px;
            }
            .scrollable-table::-webkit-scrollbar-track {
              background: transparent;
            }
            .scrollable-table::-webkit-scrollbar-thumb {
              background-color: #E5E7EB;
              border-radius: 20px;
              border: 2px solid white;
            }
            .scrollable-table::-webkit-scrollbar-thumb:hover {
              background-color: #D1D5DB;
            }
          `}
				</style>

				<div className="w-full border-b bg-gray-50">
					<table className="w-full text-left">
						<thead>
							<tr>
								<th className="px-6 py-3 text-sm font-semibold w-1/2">
									File Name
								</th>
								<th className="px-6 py-3 text-sm font-semibold w-1/4">Type</th>
								<th className="px-6 py-3 text-sm font-semibold w-1/4">
									Created At
								</th>
							</tr>
						</thead>
					</table>
				</div>
				<div className="flex-grow overflow-y-auto scrollable-table max-h-96">
					<table className="w-full text-left">
						<tbody>
							{!locked && (
								<DragAndDropFileUploadRow
									colSpan={4}
									selectedFile={selectedFile}
									setSelectedFile={setSelectedFile}
									handleUpload={handleUpload}
								/>
							)}
							{isLoadingFiles ? (
								// Skeleton loading or similar
								Array(5)
									.fill(0)
									.map((_, i) => (
										<tr key={i} className="border-b">
											<td className="px-6 py-4">
												<div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
											</td>
											<td className="px-6 py-4">
												<div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse" />
											</td>
											<td className="px-6 py-4">
												<div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse" />
											</td>
										</tr>
									))
							) : files.length === 0 ? (
								<tr>
									<td colSpan={3} className="px-6 py-4 text-center">
										No files in this folder
									</td>
								</tr>
							) : (
								files.map((file) => <FileRow key={file.id} file={file} folderId={folderId} onDeleteFile={handleDeleteFile} />)
							)}
						</tbody>
					</table>
				</div>
			</div>
			{uploading && (
				<Modal
					show={true}
					onHide={() => {}}
					centered
					backdrop="static"
					keyboard={false}
				>
					<Modal.Header>
						<Modal.Title>Uploading File</Modal.Title>
					</Modal.Header>
					<Modal.Body className="text-center">
						<Spinner animation="border" role="status" />
						<p className="mt-3">Your file is being uploaded. Please wait...</p>
					</Modal.Body>
				</Modal>
			)}
		</div>
	);
}
